/**
 * 一次データの読み方（Issue #497）。**`OcgGetState` / `OcgGetReplay` が返す形の正本はここ**で、構造の読み方（イベントのキー・op の引き方・
 * head の取り出し）はここの関数を通す（演出の画面・タイミング調節）。**入力画面の問い合わせの読み方と答え方はサーバーの `input`**
 * （server/synchro_server/ocg/input_view.py。Issue #663〜#667 で ocgCore の判断の関数は消した）。
 * イベントの `data` の中身の解釈（演出ファイルが読む move の理由・battle の値など）はモデルそのもので、ここでは扱わない。
 *
 * 形（サービス ocgcore/service と サーバー ocg/store.py が作る）:
 *   - `state`（OcgGetState の body）: `events` / `board_steps` / `card_info` / `injections` / `board`（duel_info 互換）/ `raw_board` / `pending` / `stopped`
 *   - `events[]`: { index（MSG の通し番号）, id（turn-chain-seq。打ち直しでずれるのでキーには使わない）, kind: 'event' | 'prompt',
 *       type（正規化イベント名。prompt は msg 名）, data, op_seq（答えた op の seq。最初の応答より前は null）, msg_offset（その op の区間の中の番号）,
 *       msg_index（prompt: その process() の前までの MSG 数）, turn, phase }
 *   - `pending`: 今の問い合わせ { msg, msg_index, digest, data, card_names }（無ければ null）
 *   - `hold`: 登録待ち（`hold_unconfirmed` を立てて取ったときだけ。Issue #612）{ kind, seq, msg_index, code | value }。最後の応答より後ろで
 *       未確認のカードが使われたイベントでサービスが止めた記録。あるときは pending は null
 *   - `injections.draws[]`: { kind: 'draw0' | 'draw1', seq, code, injected, msg_index }。`injections.randoms[]`: { kind: 'coin' | 'dice' | 'random', seq, value, select? }
 *   - `board_steps`: 盤面の変化の列（`fx/boardTimeline.js` が畳む）
 *   - `card_info`: `{ code: { type, level, attribute, race, atk, def } }`（cards.cdb。events に出てくるカードだけ。Issue #647）
 *   - `replay`（OcgGetReplay）: { replay: head { replay_id, game_uuid, start_video_ms, first_player, start_hand, start_lp }, ops[], injections[] }
 *   - `ops[]`: { seq（0 から隙間なし）, op_uuid（replay 内で一意）, video_ms（答えた時刻。事実）, payload, choice }
 *   - `replay.injections[]`: 決定注入キュー { kind, seq, value, note（"core" = core が決めた仮の値 = 未確認）}
 *   - `board`（duel_info 互換）: { turn, phase（'Main Phase' などの表記）, turn_p, player: [{ life }], hand / monster_field / magic_trap / grave / removed: [P1[], P2[]] }
 *
 * 確認済み = その注入の note が "core" 以外（人が選んだ・「このままで合っている」で確定した行。旧データの null も）。
 * 注入が無い行と、差し替えのとき core が決めた値で埋めた行（note="core"）は未確認。サービスの injected は埋めた行も true になるので使わない。
 */

const isFin = Number.isFinite;

// ---- events / ops ----

/**
 * 各イベントのキー（並び順どおりの配列。キーを持てないものは null）。サーバー ocg/fx_docs.py と同じ規則。
 *   - 最初の prompt より前: `start:{msg_offset}`
 *   - 応答 op が決まっているもの: `{op_uuid}:{msg_offset}`
 *   - まだ答えていない prompt（末尾）: null
 * event_id（turn-chain-seq）は操作列の途中の打ち直し・応答の変更でずれるのでキーには使わない。
 */
export function eventKeys(events = [], ops = []) {
  const uuidBySeq = new Map(ops.map((o) => [o.seq, o.op_uuid]));
  let seenPrompt = false;
  return events.map((ev) => {
    if (ev.kind === 'prompt') seenPrompt = true;   // prompt 自身から op の区間が始まる
    const off = ev.msg_offset ?? 0;
    if (ev.op_seq == null) return seenPrompt ? null : `start:${off}`;
    const uuid = uuidBySeq.get(ev.op_seq);
    return uuid ? `${uuid}:${off}` : null;
  });
}

/** キーが「最初の応答より前」のものか */
export const isStartKey = (key) => String(key ?? '').startsWith('start:');

/** seq → op */
export const opBySeq = (ops = []) => new Map(ops.map((o) => [o.seq, o]));

/** op の答えた時刻（有限なら数値、無ければ null） */
export const opVideoMs = (op) => (op && isFin(op.video_ms) ? op.video_ms : null);

// ---- replay head ----

/** replay の head { startVideoMs, firstPlayer, startHand, startLp }。replay が無ければ null */
export function replayHead(replay) {
  const h = replay?.replay;
  if (!h) return null;
  return { startVideoMs: h.start_video_ms ?? null, firstPlayer: h.first_player ?? 0, startHand: h.start_hand ?? 5, startLp: h.start_lp ?? 8000 };
}
